import { useEffect, useMemo, useState, type FormEvent } from "react";
import { motion } from "framer-motion";
import { LogIn, ShieldCheck, Mail, ArrowLeft, Fingerprint, KeyRound } from "lucide-react";
import { AccentButton, AuthScene, Button, GoogleSignInButton, Input, Label, useGoogleOneTap } from "@wa/ui";
import type { AuthLayoutSpec } from "@wa/core/theme";
import { useT, useI18n } from "../i18n/I18nProvider";
import {
  login,
  forgotPassword,
  isMfaRequired,
  googleConfig,
  googleOneTap,
  GOOGLE_REDIRECT_URL,
  type AccountUser,
  type LoginResult,
  type GoogleAuthConfig,
} from "../lib/auth";
import { mfaApi, passkeyApi, isWebAuthnReady, isWebAuthnCancel, type MfaChallengeInfo } from "../lib/security";
import { readFreshStash } from "../lib/onboardingStash";
import { Logo } from "../components/Logo";
import { AppBackground } from "../components/AppBackground";
import { LanguageMenu } from "../components/LanguageMenu";
import { ThemeToggle } from "../components/ThemeToggle";
import { isClientPortalHost } from "../lib/brand";

export function LoginPage({
  onSuccess,
  onSignupClick,
  authSpec,
}: {
  onSuccess: (u: AccountUser) => void;
  onSignupClick?: () => void;
  authSpec?: AuthLayoutSpec;
}) {
  const t = useT();
  const { locale } = useI18n();
  const glow = authSpec?.glow ?? "aurora";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [bioBusy, setBioBusy] = useState(false);
  const [bioError, setBioError] = useState(false);
  const passkeySupported = isWebAuthnReady();

  // Password reset ("forgot password") - inline view (like the MFA challenge).
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  function openForgot() {
    setForgotEmail(email);
    setForgotSent(false);
    setForgotMode(true);
  }

  async function submitForgot(e: FormEvent) {
    e.preventDefault();
    setForgotBusy(true);
    try {
      await forgotPassword(forgotEmail.trim(), locale);
      // Anti-enumeration: show the confirmation regardless of whether the account exists.
      setForgotSent(true);
    } catch {
      // Show the neutral confirmation on a network error too (leaks no information).
      setForgotSent(true);
    } finally {
      setForgotBusy(false);
    }
  }

  // Google login (host-level setting) + redirect-flow error message from the URL.
  const [googleCfg, setGoogleCfg] = useState<GoogleAuthConfig>({ enabled: false, clientId: "" });
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    googleConfig().then(setGoogleCfg).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    const code = params.get("authError");
    if (code) {
      const known = ["no_account", "email_unverified", "google_denied"];
      // On the portal the same failure has a different remedy: there is no
      // "sign up" to point at, only the invitation the client was sent.
      const key = code === "no_account" && isClientPortalHost() ? "no_accountPortal" : code;
      setAuthError(known.includes(code) ? t(`login.authErrors.${key}`) : t("login.authErrors.generic"));
      params.delete("authError");
      params.delete("authOk");
      params.delete("welcome");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** One Tap credential → sign-in (OAuth bypasses MFA). */
  async function handleGoogleCredential(credential: string) {
    setBusy(true);
    setAuthError(null);
    try {
      onSuccess(await googleOneTap(credential));
    } catch {
      setAuthError(t("login.authErrors.generic"));
      setBusy(false);
    }
  }

  // MFA challenge (second factor)
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaError, setMfaError] = useState(false);
  const [mfaInfo, setMfaInfo] = useState<MfaChallengeInfo | null>(null);
  const [emailSent, setEmailSent] = useState<string | null>(null);

  // If an onboarding stash from the public "Start" funnel is pending (the user started
  // in the funnel and jumped here "back to login"), do not auto-prompt One Tap: One Tap creates
  // the account via the JSON path (POST /onetap → buildMe, WITHOUT the ?authOk=google&welcome=1 redirect),
  // which the `main.tsx` stash consumer does not see → the funnel choice (template/caps/brief) would be lost
  // silently. The redirect button, by contrast, lands with welcome=1 and consumes the stash correctly,
  // so we steer the user there. (Review finding #1.) No-op when there is no fresh stash.
  const funnelStashPending = useMemo(() => Boolean(readFreshStash()), []);

  // One Tap prompt (dribbble style) - only when Google is enabled and not in the MFA flow.
  useGoogleOneTap({
    clientId: googleCfg.enabled ? googleCfg.clientId : "",
    onCredential: handleGoogleCredential,
    disabled: !!pendingToken || funnelStashPending,
  });

  useEffect(() => {
    if (!pendingToken) {
      setMfaInfo(null);
      setEmailSent(null);
      return;
    }
    mfaApi.challengeInfo(pendingToken).then(setMfaInfo).catch(() => setMfaInfo(null));
  }, [pendingToken]);

  /** Login response → either the MFA gate or a successful sign-in. */
  function handleResult(res: LoginResult) {
    if (isMfaRequired(res)) setPendingToken(res.pendingToken);
    else onSuccess(res);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(false);
    try {
      handleResult(await login(email, password));
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }

  async function biometric() {
    setBioBusy(true);
    setBioError(false);
    try {
      // The entered email (if given) lets the server send allowCredentials -
      // the browser goes straight to Touch ID, skipping the passkey picker.
      handleResult(await passkeyApi.login(email.trim() || undefined));
    } catch (err) {
      // Cancel / timeout / unsuitable passkey is not a real error - do not show the banner.
      if (!isWebAuthnCancel(err)) setBioError(true);
    } finally {
      setBioBusy(false);
    }
  }

  async function submitMfa(e: FormEvent) {
    e.preventDefault();
    if (!pendingToken) return;
    setBusy(true);
    setMfaError(false);
    try {
      onSuccess(await mfaApi.verify(pendingToken, mfaCode.trim()));
    } catch {
      setMfaError(true);
    } finally {
      setBusy(false);
    }
  }

  async function sendMfaEmail() {
    if (!pendingToken) return;
    setBusy(true);
    try {
      const res = await mfaApi.emailSendLogin(pendingToken);
      setEmailSent(res.sentTo ?? "");
    } finally {
      setBusy(false);
    }
  }

  function cancelMfa() {
    setPendingToken(null);
    setMfaCode("");
    setMfaError(false);
  }

  return (
    <AuthScene
      layout={authSpec?.layout}
      background={<AppBackground spec={authSpec} />}
      logo={<Logo variant="full" className="h-11 w-auto text-foreground md:h-12" />}
      topRight={
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <LanguageMenu />
        </div>
      }
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="card-aurora-border rounded-xl bg-card/95 p-6 shadow-soft">
          {pendingToken ? (
            <form onSubmit={submitMfa} className="flex flex-col gap-4">
              <div className="flex flex-col items-center gap-2 text-center">
                <ShieldCheck className="size-8 text-primary" />
                <h1 className="text-xl font-bold tracking-tight">{t("mfa.challengeTitle")}</h1>
                <p className="text-sm text-muted-foreground">{t("mfa.challengeHint")}</p>
              </div>
              <Input
                autoFocus
                inputMode="text"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                placeholder={t("mfa.codePlaceholder")}
                className="text-center font-mono text-lg tracking-widest"
              />
              {mfaError && <p className="text-sm text-destructive">{t("mfa.invalidCode")}</p>}
              <AccentButton glow={glow} type="submit" size="md" loading={busy} disabled={!mfaCode.trim()} className="w-full">
                {t("mfa.verify")}
              </AccentButton>

              {mfaInfo?.methods.email_otp &&
                (emailSent !== null ? (
                  <p className="text-center text-xs text-muted-foreground">
                    {t("mfa.emailSent", { email: emailSent || t("mfa.yourEmail") })}
                  </p>
                ) : (
                  <Button type="button" variant="outline" size="sm" onClick={sendMfaEmail} disabled={busy} className="self-center">
                    <Mail className="mr-1 size-4" />
                    {t("mfa.sendEmailCode")}
                  </Button>
                ))}

              <Button type="button" variant="ghost" size="sm" onClick={cancelMfa} className="self-center">
                <ArrowLeft className="mr-1 size-4" />
                {t("mfa.back")}
              </Button>
            </form>
          ) : forgotMode ? (
            <form onSubmit={submitForgot} className="flex flex-col gap-4">
              <div className="flex flex-col items-center gap-2 text-center">
                <KeyRound className="size-8 text-primary" />
                <h1 className="text-xl font-bold tracking-tight">{t("forgot.title")}</h1>
                <p className="text-sm text-muted-foreground">{t("forgot.subtitle")}</p>
              </div>
              {forgotSent ? (
                <p className="rounded-md bg-primary/10 p-3 text-center text-sm text-foreground ring-1 ring-primary/20">
                  {t("forgot.sent")}
                </p>
              ) : (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="forgot-email">{t("login.email")}</Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      autoComplete="username"
                      autoFocus
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                    />
                  </div>
                  <AccentButton glow={glow} type="submit" size="md" loading={forgotBusy} disabled={!forgotEmail.trim()} className="w-full">
                    <Mail className="mr-1 size-4" />
                    {forgotBusy ? t("forgot.busy") : t("forgot.submit")}
                  </AccentButton>
                </>
              )}
              <Button type="button" variant="ghost" size="sm" onClick={() => setForgotMode(false)} className="self-center">
                <ArrowLeft className="mr-1 size-4" />
                {t("forgot.back")}
              </Button>
            </form>
          ) : (
            <>
              <div className="mb-1 flex items-center gap-3">
                <div className="inline-flex size-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <LogIn className="size-5" />
                </div>
                <h1 className="text-2xl font-bold tracking-tight">{t("login.title")}</h1>
              </div>
              <p className="mb-6 text-sm text-muted-foreground">
                {t(isClientPortalHost() ? "login.subtitlePortal" : "login.subtitle")}
              </p>

              {authError && (
                <div className="mb-4 rounded-md bg-destructive/10 p-3 text-sm font-medium text-destructive ring-1 ring-destructive/20">
                  {authError}
                </div>
              )}

              <form onSubmit={submit} className="flex flex-col gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email">{t("login.email")}</Label>
                  <Input id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">{t("login.password")}</Label>
                    <button
                      type="button"
                      onClick={openForgot}
                      className="text-xs font-medium text-muted-foreground hover:text-primary hover:underline"
                    >
                      {t("login.forgotPassword")}
                    </button>
                  </div>
                  <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                </div>
                {error && <p className="text-sm text-destructive">{t("login.error")}</p>}
                <AccentButton glow={glow} type="submit" size="md" loading={busy} className="mt-1 w-full">
                  {busy ? t("login.busy") : t("login.submit")}
                </AccentButton>
              </form>

              {(passkeySupported || googleCfg.enabled) && (
                <>
                  <div className="my-5 flex items-center gap-3">
                    <span className="h-px flex-1 bg-border" />
                    <span className="text-xs text-muted-foreground">{t("login.or")}</span>
                    <span className="h-px flex-1 bg-border" />
                  </div>
                  <div className="flex flex-col gap-3">
                    {googleCfg.enabled && (
                      <GoogleSignInButton href={GOOGLE_REDIRECT_URL} label={t("login.google")} disabled={busy} />
                    )}
                    {passkeySupported && (
                      <Button type="button" variant="outline" size="md" className="w-full" loading={bioBusy} disabled={busy} onClick={biometric}>
                        <Fingerprint className="mr-1 size-4" />
                        {t("login.passkey")}
                      </Button>
                    )}
                  </div>
                  {bioError && <p className="mt-2 text-center text-sm text-destructive">{t("login.passkeyError")}</p>}
                </>
              )}

              {/* No sign-up on the portal. Since a portal contact is linked only by
                  invitation (see api portal/lib/link.ts), a self-made account here
                  would land the client in an empty Husly hub and look broken. */}
              {onSignupClick && !isClientPortalHost() && (
                <div className="mt-8 text-center text-sm">
                  <span className="text-muted-foreground">{t("login.noAccount")} </span>
                  <button
                    type="button"
                    onClick={onSignupClick}
                    className="font-medium text-primary hover:underline"
                  >
                    {t("login.createAccount")}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>
    </AuthScene>
  );
}
